import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Dialog, Stack, Typography } from '@mui/material';
import { GridRenderCellParams, GridSortModel } from '@mui/x-data-grid-premium';

import dayjs from 'dayjs';

import { ReactComponent as AddCircleIcon } from '@/assets/icons/add-circle.svg';
import { ReactComponent as BoxIcon } from '@/assets/icons/box-outline-icon.svg';
import { ReactComponent as TruckLightIcon } from '@/assets/icons/truck-light-icon.svg';
import { ReactComponent as TruckIcon } from '@/assets/icons/truck-outline-icon.svg';
import AppButton from '@/shared/ui/AppButton';
import DialogCloseButton from '@/shared/ui/DialogCloseButton';
import {
  FilterFieldConfig,
  FilterValues,
  GeneratikTable,
  ToolbarPrimaryButton,
  getColumnsFitWidth,
  getDialogTableWidth,
  getRowsTableHeight,
  useGeneratikTableFilters,
} from '@/shared/ui/GeneratikTable';
import { FONT_FAMILY } from '@/shared/ui/form-fields/fieldStyles';

import { useShipmentLabel } from '../../../api/useShipmentLabel';
import {
  usePendingPickupsQuery,
  usePickupOrdersQuery,
  usePickupPointsQuery,
  useShipmentQuery,
  useShipmentsSearchQuery,
} from '../../../api/useShippingQueries';
import {
  PendingPickupGroup,
  PendingPickupRow,
  buildPendingPickups,
  filterPendingPickupRows,
  isPastPickupDate,
  resolveSelectedGroup,
  resolveSelectedWaitingGroup,
  searchPendingPickupRows,
} from '../../../model/pendingPickups';
import AwbDialogHeader from '../AwbDialogHeader';
import PlacePickupOrderDialog from '../PlacePickupOrderDialog';
import ShipmentDetailsDialog from '../ShipmentDetailsDialog';
import VoidShipmentDialog from '../VoidShipmentDialog';
import { HeaderIcon, generateAwbResultPaperSx } from '../styles';
import PendingPickupGroupCell from './PendingPickupGroupCell';
import usePendingPickupsColumns from './usePendingPickupsColumns';

interface PendingPickupsDialogProps {
  onClose: () => void;
  onViewAll?: () => void;
  onGenerate?: () => void;
  onCourierOrdered?: () => void;
  width?: string;
}

const SENDER_FILTER = 'sender';
const TABLE_HEIGHT = getRowsTableHeight(10);
const CREATED_AT_FETCH_SIZE = 500;

const isLeafRow = (row: PendingPickupRow) => !!row.shipment;
const GROUPING_COLUMN_WIDTH = 76;
const CHECKBOX_COLUMN_WIDTH = 50;
const EMPTY_PAPER_SX = {
  ...generateAwbResultPaperSx,
  width: 'calc(100% - 64px)',
  maxWidth: '61rem',
  height: 'min(39.875rem, 90dvh)',
  position: 'relative',
} as const;

const EMPTY_TITLE_SX = {
  fontFamily: FONT_FAMILY,
  fontSize: '1.125rem',
  fontWeight: 400,
  lineHeight: 1.25,
  color: 'grey.950',
  textAlign: 'center',
} as const;

const EMPTY_BODY_SX = {
  fontFamily: FONT_FAMILY,
  fontSize: '0.75rem',
  fontWeight: 350,
  lineHeight: 1.25,
  color: 'grey.800',
  textAlign: 'center',
  maxWidth: '27.4375rem',
} as const;

const PendingPickupsDialog: FC<PendingPickupsDialogProps> = ({
  onClose,
  onViewAll,
  onGenerate,
  onCourierOrdered,
  width,
}) => {
  const { t } = useTranslation();
  const { openLabel } = useShipmentLabel();

  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [filterValues, setFilterValues] = useState<FilterValues>({});
  const [search, setSearch] = useState('');
  const [selectedRows, setSelectedRows] = useState<PendingPickupRow[]>([]);
  const [orderGroup, setOrderGroup] = useState<PendingPickupGroup | null>(null);
  const [voidRow, setVoidRow] = useState<PendingPickupRow | null>(null);
  const [viewedId, setViewedId] = useState<string | null>(null);

  const today = useMemo(() => dayjs().format('YYYY-MM-DD'), []);
  const { data: waiting = [], isLoading: waitingLoading } = usePendingPickupsQuery();
  const { data: orderedPage, isLoading: orderedLoading } = usePickupOrdersQuery({
    status: 'PLACED',
    from: today,
    page: 0,
    size: 100,
  });
  const { data: pickupPoints = [] } = usePickupPointsQuery();
  const { data: viewedShipment } = useShipmentQuery(viewedId ?? undefined, !!viewedId);

  const senderNameByClientId = useMemo(
    () =>
      pickupPoints.reduce<Record<string, string>>((map, point) => {
        map[point.fanClientId] = point.name ?? point.fanClientId;
        return map;
      }, {}),
    [pickupPoints],
  );

  const { groups, rows: allRows } = useMemo(
    () => buildPendingPickups(waiting, orderedPage?.content ?? [], senderNameByClientId),
    [orderedPage, senderNameByClientId, waiting],
  );

  const isEmpty = !waitingLoading && !orderedLoading && allRows.length === 0;

  const groupsByKey = useMemo(() => new Map(groups.map((group) => [group.key, group])), [groups]);

  const selectedSenders = filterValues[SENDER_FILTER];
  const rows = useMemo(
    () =>
      searchPendingPickupRows(
        filterPendingPickupRows(
          allRows,
          Array.isArray(selectedSenders) ? selectedSenders : [],
          groups,
        ),
        search,
        groups,
      ),
    [allRows, groups, search, selectedSenders],
  );

  const filterFields = useMemo<FilterFieldConfig[]>(() => {
    const senders = Array.from(new Set(groups.map((group) => group.order.clientId)));
    return [
      {
        id: SENDER_FILTER,
        label: t('Pickup point'),
        type: 'autocomplete',
        multiple: true,
        options: senders.map((clientId) => ({
          value: clientId,
          label: senderNameByClientId[clientId] ?? clientId,
        })),
      },
    ];
  }, [groups, senderNameByClientId, t]);

  const { handleFilterApply, handleFilterReset } = useGeneratikTableFilters({
    filterFields,
    filterValues,
    setFilterValues,
  });

  const selectedWaitingGroup = resolveSelectedWaitingGroup(selectedRows, groups);
  const courierAlreadyOrdered = !!resolveSelectedGroup(selectedRows, groups)?.ordered;
  const pickupDatePassed =
    !!selectedWaitingGroup && isPastPickupDate(selectedWaitingGroup.order.pickupDate);
  const canOrderCourier = !!selectedWaitingGroup && !pickupDatePassed;

  const rightButtons = useMemo<ToolbarPrimaryButton[]>(
    () => [
      ...(onViewAll
        ? [
            {
              id: 'view-all-awbs',
              label: t('awb.view_all'),
              onClick: onViewAll,
              dataTestId: 'pending-pickups-view-all',
            },
          ]
        : []),
      {
        id: 'order-courier',
        label: t('Order a courier'),
        icon: <TruckLightIcon />,
        disabled: !canOrderCourier,
        tooltip: courierAlreadyOrdered
          ? t('awb.order_courier_already_ordered_hint')
          : pickupDatePassed
            ? t('awb.order_courier_past_date_hint')
            : selectedWaitingGroup
              ? undefined
              : t('awb.order_courier_select_hint'),
        onClick: () =>
          selectedWaitingGroup && !pickupDatePassed && setOrderGroup(selectedWaitingGroup),
        dataTestId: 'pending-pickups-order-courier',
      },
      ...(onGenerate
        ? [
            {
              id: 'generate-awb',
              label: t('Generate AWB'),
              icon: <AddCircleIcon />,
              onClick: onGenerate,
              dataTestId: 'pending-pickups-generate',
            },
          ]
        : []),
    ],
    [
      canOrderCourier,
      courierAlreadyOrdered,
      onGenerate,
      onViewAll,
      pickupDatePassed,
      selectedWaitingGroup,
      t,
    ],
  );

  const { data: shipmentsPage } = useShipmentsSearchQuery({ page: 0, size: CREATED_AT_FETCH_SIZE });
  const createdAtById = useMemo(
    () =>
      (shipmentsPage?.content ?? []).reduce<Record<string, string>>((map, shipment) => {
        map[shipment.id] = shipment.generatedAt ?? shipment.createdAt;
        return map;
      }, {}),
    [shipmentsPage],
  );
  const { columns, sortOptions } = usePendingPickupsColumns(createdAtById);
  const tableWidth = CHECKBOX_COLUMN_WIDTH + GROUPING_COLUMN_WIDTH + getColumnsFitWidth(columns);

  const handlePrint = useCallback(
    (row: PendingPickupRow) => openLabel(row.shipment.id),
    [openLabel],
  );
  const handleView = useCallback((row: PendingPickupRow) => setViewedId(row.shipment.id), []);
  const handleVoid = useCallback((row: PendingPickupRow) => setVoidRow(row), []);

  const renderGroupingCell = useCallback(
    (params: GridRenderCellParams) => (
      <PendingPickupGroupCell
        params={params}
        group={groupsByKey.get(
          String(params.rowNode.type === 'group' ? params.rowNode.groupingKey : ''),
        )}
        onPrint={handlePrint}
        onView={handleView}
        onVoid={handleVoid}
      />
    ),
    [groupsByKey, handlePrint, handleView, handleVoid],
  );

  const getRowId = useCallback((row: PendingPickupRow) => row.rowId, []);

  const handleSelectedRowsChange = useCallback(
    (selected: PendingPickupRow[]) => setSelectedRows(selected.filter((row) => !!row?.shipment)),
    [],
  );

  return (
    <>
      <Dialog
        open
        onClose={onClose}
        maxWidth={false}
        PaperProps={{
          'data-testid': 'pending-pickups-dialog',
          sx: isEmpty
            ? EMPTY_PAPER_SX
            : {
                ...generateAwbResultPaperSx,
                width: width ?? getDialogTableWidth(tableWidth),
                maxWidth: 'calc(100% - 2rem)',
              },
        }}
      >
        {isEmpty ? (
          <>
            <DialogCloseButton
              onClick={onClose}
              sx={{ position: 'absolute', top: '1rem', right: '1rem', mr: 0 }}
              data-testid="pending-pickups-close"
            />
            <Stack
              flex={1}
              alignItems="center"
              justifyContent="center"
              gap="0.5rem"
              data-testid="pending-pickups-empty"
            >
              <HeaderIcon sx={{ width: '3.125rem', height: '3.125rem' }}>
                <BoxIcon style={{ width: 32, height: 32 }} />
              </HeaderIcon>
              <Typography sx={EMPTY_TITLE_SX}>{t('awb.waiting_pickup_empty_title')}</Typography>
              <Typography sx={EMPTY_BODY_SX}>{t('awb.waiting_pickup_empty_body')}</Typography>
              <Stack direction="row" gap="0.5rem" mt="0.5rem">
                {!!onGenerate && (
                  <AppButton
                    variant="primary"
                    size="small"
                    onClick={onGenerate}
                    data-testid="pending-pickups-empty-generate"
                  >
                    {t('Generate AWB')}
                  </AppButton>
                )}
                {!!onViewAll && (
                  <AppButton
                    variant="secondary"
                    size="small"
                    onClick={onViewAll}
                    data-testid="pending-pickups-empty-view-delivery"
                  >
                    {t('awb.view_in_delivery')}
                  </AppButton>
                )}
              </Stack>
            </Stack>
          </>
        ) : (
          <>
            <AwbDialogHeader
              icon={<TruckIcon />}
              title={t('awb.waiting_pickup_title')}
              subtitle={t('awb.waiting_pickup_subtitle')}
              subtitleNoWrap={false}
              onClose={onClose}
              closeTestId="pending-pickups-close"
            />

            <GeneratikTable<PendingPickupRow>
              columns={columns}
              rows={rows}
              rowCount={rows.length}
              getRowId={getRowId}
              loading={waitingLoading || orderedLoading}
              paginationMode="client"
              sortingMode="client"
              sortModel={sortModel}
              onSortModelChange={setSortModel}
              sortOptions={sortOptions}
              showSearch
              searchValue={search}
              onSearchChange={setSearch}
              searchPlaceholder={t('awb.search_placeholder')}
              showColumns={false}
              filterFields={filterFields}
              filterValues={filterValues}
              onFilterValuesChange={setFilterValues}
              onFilterApply={handleFilterApply}
              onFilterReset={handleFilterReset}
              rightButtons={rightButtons}
              checkboxSelection
              selectDescendants
              onSelectedRowsChange={handleSelectedRowsChange}
              groupByField="groupKey"
              renderGroupingCell={renderGroupingCell}
              groupingColumnWidth={GROUPING_COLUMN_WIDTH}
              isRowCheckboxHidden={isLeafRow}
              emptyStateMessage={t('No AWB is waiting for a courier')}
              height={TABLE_HEIGHT}
              autosizeOnMount={false}
              noTopPadding
              noPadding
              plainToolbar
            />
          </>
        )}
      </Dialog>

      {!!orderGroup && (
        <PlacePickupOrderDialog
          open
          group={orderGroup.order}
          onClose={() => setOrderGroup(null)}
          onPlaced={onCourierOrdered}
        />
      )}

      {!!voidRow && (
        <VoidShipmentDialog
          open
          shipmentId={voidRow.shipment.id}
          awbNumber={voidRow.shipment.awbNumber}
          recipientLabel={[voidRow.shipment.recipientName, voidRow.shipment.recipientLocality]
            .filter(Boolean)
            .join(', ')}
          onClose={() => setVoidRow(null)}
        />
      )}

      {!!viewedId && !!viewedShipment && (
        <ShipmentDetailsDialog shipment={viewedShipment} onClose={() => setViewedId(null)} />
      )}
    </>
  );
};

export default PendingPickupsDialog;
